import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Catches render/lifecycle errors anywhere below it so a single bad field
 * cannot white-screen the whole Growth Hub.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full text-left">
          <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-3">
            Something went wrong on this page
          </h1>
          <p className="text-sm text-slate-600 mb-2">
            Your work is saved &mdash; this screen just failed to render. Reloading
            almost always fixes it.
          </p>
          <p className="text-xs font-mono text-slate-400 mb-8 break-words">
            {this.state.error?.message || 'Unknown error'}
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => window.location.reload()}
              data-testid="error-boundary-reload-button"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload page
            </Button>
            <Button
              variant="outline"
              onClick={() => { window.location.href = '/'; }}
              data-testid="error-boundary-home-button"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to Hub
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
